import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';
import PasswordRequirements from '../components/PasswordRequirements';
import './ResetPassword.css';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userName, setUserName] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. No token provided.');
      setValidating(false);
      return;
    }

    // Validate token on mount
    const validateToken = async () => {
      try {
        const response = await api.post('/auth/validate-reset-token', { token });
        if (response.data.success) {
          setTokenValid(true);
          setUserName(response.data.userName);
        } else {
          setError(response.data.message);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Invalid or expired reset link');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/reset-password', {
        token,
        newPassword
      });

      if (response.data.success) {
        setMessage(response.data.message);
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-logo-container">
          <img src="/logo.png" alt="Hanwha Power" className="login-logo-img" />
        </div>
        <div className="reset-password-header-bar">Reset Password</div>
        <div className="reset-password-content">
          <div className="reset-password-box">
            <p className="validating-message">Validating reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid && !validating) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-logo-container">
          <img src="/logo.png" alt="Hanwha Power" className="login-logo-img" />
        </div>
        <div className="reset-password-header-bar">Reset Password</div>
        <div className="reset-password-content">
          <div className="reset-password-box">
            <div className="error-message-box">
              <span className="error-icon">⚠</span>
              <div>
                <strong>Invalid Reset Link</strong>
                <p>{error}</p>
              </div>
            </div>
            <div className="form-group">
              <Link to="/forgot-password" className="action-button">
                Request New Reset Link
              </Link>
            </div>
            <div className="back-to-login">
              <Link to="/login">← Back to Login</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-logo-container">
        <img src="/logo.png" alt="Hanwha Power" className="login-logo-img" />
      </div>

      <div className="reset-password-header-bar">Reset Password</div>

      <div className="reset-password-content">
        <div className="reset-password-box">
          {!success ? (
            <>
              <h2>Create New Password</h2>
              {userName && (
                <p className="user-greeting">Hello, {userName}</p>
              )}
              <p className="reset-password-instruction">
                Please enter your new password below.
              </p>

              {error && (
                <div className="error-message-box">
                  <span className="error-icon">⚠</span>
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={loading}
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <PasswordRequirements password={newPassword} />

                <div className="form-group">
                  <button 
                    type="submit" 
                    className="reset-button" 
                    disabled={loading}
                  >
                    {loading ? 'Resetting Password...' : 'Reset Password'}
                  </button>
                </div>
              </form>

              <div className="back-to-login">
                <Link to="/login">← Back to Login</Link>
              </div>
            </>
          ) : (
            <>
              <div className="success-message-box">
                <span className="success-icon">✓</span>
                <h2>Password Reset Successful!</h2>
              </div>
              <p className="success-instruction">
                {message}
              </p>
              <p className="success-instruction">
                Redirecting to login page...
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
